import { filter, find, findIndex, includes, set, some, sumBy, without } from 'lodash';
import { TOrder } from 'types/Orders/order';



import { PayloadAction, createSlice } from '@reduxjs/toolkit';



import { EmptyRows, initialOrderData } from '../constants';


const initialState = {
    data: initialOrderData,
    orderDiffs: null,
    totalProducts: 0,
    totalServices: 0,
    totalTasks: 0,
    selectedServices: [],
    selectedProducts: [],
    selectedTasks: [],
    selectedWorksheet: null,
    immutableOrderData: null,
    orderObjects: [],
    mutableOrderFields: [],
    immutableOrderFields: [],
    mutableRequiredOrderFields: [],
    immutableRequiredOrderFields: [],
    updatedRowIndex: null,
    searchValue: '',
    selectedItemsFromSearchTabs: {
        products: [],
        services: [],
        tasks: [],
        combo: [],
        hammer: [],
    },
    /**
     * Порядок выбора из поиска: ключи «вкладка:id», новые — в конце. Сами
     * позиции разложены по вкладкам, а список выбранного показываем по времени
     * добавления, поэтому порядок хранится рядом, не трогая раскладку.
     */
    selectedItemsOrder: [] as string[],

    /**
     * Запрос на добавление сущности из таблицы: какую вкладку открыть в поиске и
     * куда вернуть прокрутку, когда с добавлением закончили. `scrollOffset` —
     * положение страницы в момент нажатия «Добавить», `buttonId` по нему же
     * находит нужную прокручиваемую область. `null` — обычная работа с поиском,
     * никто его не вызывал.
     */
    entityAddRequest: null as {
        tabKey: string;
        buttonId: string;
        scrollOffset: number;
    } | null,

    productByBarcode: null,

    // Сущности без исполнителей
    selectedUnassignedEntities: [],
    totalUnassignedEntities: 0,

    isAppointPerformerModalVisible: false,
};

const orderSlice = createSlice({
    name: 'order',
    initialState,
    reducers: {
        setOrderData: (state, action: PayloadAction<TOrder>) => {
            const { payload } = action;
            state.data = payload;

            state.totalProducts = sumBy(state.data.products, 'total');
            state.totalServices = sumBy(state.data.services, 'total');
            state.totalTasks = sumBy(state.data.tasks, 'total');
            state.totalUnassignedEntities = sumBy(
                filter(state.data.unassigned_entities, (entity) => !entity.isDuplicated),
                'total'
            );
        },

        // Управление задачами
        setTasks: (state, { payload }) => {
            state.data.tasks = payload;
            state.totalTasks = sumBy(state.data.tasks, 'total');
        },

        selectTask: (state, { payload }) => {
            if (!includes(state.selectedTasks, payload)) {
                state.selectedTasks.push(payload);
                return;
            }
            state.selectedTasks = without(state.selectedTasks, payload);
        },

        deleteSelectedTasks: (state) => {
            state.data.tasks = state.data.tasks.filter(
                (item) => !state.selectedTasks.includes(item.id)
            );
            state.totalTasks = sumBy(state.data.tasks, 'total');
            state.selectedTasks = [];
        },

        selectAllTasks: (state) => {
            if (state.selectedTasks.length !== state.data.tasks.length - 2) {
                state.selectedTasks = state.data.tasks
                    .map((item) => item.id)
                    .filter((id) => id !== EmptyRows.EMPTY_ROW_1 && id !== EmptyRows.EMPTY_ROW_2);
                return;
            }
            state.selectedTasks = [];
        },

        clearSelectedTasks: (state) => {
            state.selectedTasks = [];
        },

        // Управление продуктами
        setProducts: (state, { payload }) => {
            state.data.products = payload;
            state.totalProducts = sumBy(state.data.products, 'total');
        },

        setProductByBarcode: (state, { payload }) => {
            state.productByBarcode = payload;
        },

        selectProduct: (state, { payload }) => {
            if (!includes(state.selectedProducts, payload)) {
                state.selectedProducts.push(payload);
                return;
            }
            state.selectedProducts = without(state.selectedProducts, payload);
        },

        deleteSelectedProducts: (state) => {
            state.data.products = state.data.products.filter(
                (item) => !state.selectedProducts.includes(item.id)
            );
            state.totalProducts = sumBy(state.data.products, 'total');
            state.selectedProducts = [];
        },

        selectAllProducts: (state) => {
            if (state.selectedProducts.length !== state.data.products.length - 2) {
                state.selectedProducts = state.data.products
                    .map((item) => item.id)
                    .filter((id) => id !== EmptyRows.EMPTY_ROW_1 && id !== EmptyRows.EMPTY_ROW_2);
                return;
            }
            state.selectedProducts = [];
        },

        clearSelectedProducts: (state) => {
            state.selectedProducts = [];
        },

        // Управление услугами
        setServices: (state, { payload }) => {
            state.data.services = payload;
            state.totalServices = sumBy(state.data.services, 'total');
        },

        selectService: (state, { payload }) => {
            if (!includes(state.selectedServices, payload)) {
                state.selectedServices.push(payload);
                return;
            }
            state.selectedServices = without(state.selectedServices, payload);
        },

        deleteSelectedServices: (state) => {
            state.data.services = state.data.services.filter(
                (item) => !state.selectedServices.includes(item.id)
            );
            state.totalServices = sumBy(state.data.services, 'total');
            state.selectedServices = [];
        },

        selectAllServices: (state) => {
            if (state.selectedServices.length !== state.data.services.length - 2) {
                state.selectedServices = state.data.services
                    .map((item) => item.id)
                    .filter((id) => id !== EmptyRows.EMPTY_ROW_1 && id !== EmptyRows.EMPTY_ROW_2);
                return;
            }
            state.selectedServices = [];
        },

        clearSelectedServices: (state) => {
            state.selectedServices = [];
        },

        setOrderProperty: (state, { payload }) => {
            const { key, value } = payload;
            state[key] = value;
        },

        setMutableOrderFieldValue: (state, { payload }) => {
            const { index, value } = payload;
            state.mutableOrderFields[index].value = value;

            const intersectingFieldIndex = findIndex(
                state.mutableRequiredOrderFields,
                (field) => field.id === state.mutableOrderFields[index].id
            );
            if (
                intersectingFieldIndex !== -1 &&
                !state.mutableRequiredOrderFields[intersectingFieldIndex].isOrderObject
            )
                state.mutableRequiredOrderFields[intersectingFieldIndex].value = value;
        },

        setMutableRequiredOrderFieldValue: (state, { payload }) => {
            const { index, value } = payload;
            state.mutableRequiredOrderFields[index].value = value;

            const intersectingFieldIndex = findIndex(
                state.mutableOrderFields,
                (field) => field.id === state.mutableRequiredOrderFields[index].id
            );
            if (
                intersectingFieldIndex !== -1 &&
                !state.mutableRequiredOrderFields[index].isOrderObject
            )
                state.mutableOrderFields[intersectingFieldIndex].value = value;
        },

        setOrderObject: (state, { payload }) => {
            state.data.order_object = payload;
        },

        /** Нажали «Добавить» в таблице услуг, товаров или задач. */
        setEntityAddRequest: (state, { payload }) => {
            state.entityAddRequest = payload;
        },

        setSelectedItemsFromSearchTabs: (state, { payload: { item, key } }) => {
            const orderKey = `${key}:${item.id}`;
            if (some(state.selectedItemsFromSearchTabs[key], { id: item.id })) {
                state.selectedItemsFromSearchTabs[key] = state.selectedItemsFromSearchTabs[
                    key
                ].filter((elem) => elem.id !== item.id);
                state.selectedItemsOrder = without(state.selectedItemsOrder, orderKey);
            } else {
                state.selectedItemsFromSearchTabs[key] = [
                    ...state.selectedItemsFromSearchTabs[key],
                    item,
                ];
                state.selectedItemsOrder = [...without(state.selectedItemsOrder, orderKey), orderKey];
            }
        },

        setSelectedItemsFromSearchTabsList: (state, { payload: { list, key } }) => {
            state.selectedItemsFromSearchTabs[key] = list;
        },

        resetSelectedItemsFromSearchTabs: (state) => {
            state.selectedItemsFromSearchTabs = {
                products: [],
                services: [],
                combo: [],
                hammer: [],
                tasks: [],
            };
            state.selectedItemsOrder = [];
        },

        // Временно

        setUnassignedEntity: (state, { payload }) => {
            state.data.unassigned_entities = payload;
        },

        selectUnassignedEntity: (state, { payload }) => {
            if (!find(state.selectedUnassignedEntities, (ent) => payload.id === ent.id)) {
                state.selectedUnassignedEntities.push(payload);
                return;
            }
            state.selectedUnassignedEntities = filter(
                state.selectedUnassignedEntities,
                (ent) => payload.id !== ent.id
            );
        },

        selectAllUnassignedEntities: (state) => {
            if (state.selectedUnassignedEntities.length !== state.data.unassigned_entities.length) {
                state.selectedUnassignedEntities = state.data.unassigned_entities;

                return;
            }
            state.selectedUnassignedEntities = [];
        },

        clearSelectedUnassignedEntities: (state) => {
            state.selectedUnassignedEntities = [];
        },

        setSearchValue: (state, action) => {
            state.searchValue = action.payload;
        },

        setIsAppointPerformerModalVisible: (state, { payload }) => {
            state.isAppointPerformerModalVisible = payload;
        },

        setOrderDiffs: (state, { payload }) => {
            state.orderDiffs = payload;
        },

        resetOrderState: (state) => {
            state.data = initialOrderData;
            state.totalProducts = 0;
            state.totalServices = 0;
            state.selectedServices = [];
            state.selectedProducts = [];
            state.selectedWorksheet = null;
            state.immutableOrderData = null;
            state.orderObjects = [];
            state.mutableOrderFields = [];
            state.immutableOrderFields = [];
            state.mutableRequiredOrderFields = [];
            state.immutableRequiredOrderFields = [];
            state.orderDiffs = null;
            state.entityAddRequest = null;
            state.selectedItemsFromSearchTabs = {
                products: [],
                services: [],
                combo: [],
                hammer: [],
                tasks: [],
            };
            state.selectedItemsOrder = [];
        },
    },
});

export const {
    setOrderData,
    setOrderDiffs,
    clearSelectedProducts,
    deleteSelectedProducts,
    selectAllProducts,
    selectProduct,
    setProducts,
    setProductByBarcode,
    clearSelectedTasks,
    deleteSelectedTasks,
    selectAllTasks,
    selectTask,
    setTasks,
    clearSelectedServices,
    deleteSelectedServices,
    selectAllServices,
    selectService,
    setServices,
    setOrderProperty,
    setMutableOrderFieldValue,
    setMutableRequiredOrderFieldValue,
    resetOrderState,
    setOrderObject,
    selectAllUnassignedEntities,
    selectUnassignedEntity,
    clearSelectedUnassignedEntities,
    setSelectedItemsFromSearchTabs,
    resetSelectedItemsFromSearchTabs,
    setSearchValue,
    setEntityAddRequest,
    setUnassignedEntity,
    setIsAppointPerformerModalVisible,
    setSelectedItemsFromSearchTabsList,
} = orderSlice.actions;
export default orderSlice.reducer;